import React, { useEffect, useState } from 'react';
import moment from 'moment';
import RectangleTrees from '../RectangleTrees';
import WorldMap from '@intelligence/pages/MarketProfile/components/WorldMap';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import LineChart from '@/pages/intelligence/components/Chart/components/LineChart';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { ITableData, IGenreType } from '../../typings';
import { valueFormat } from '@/pages/intelligence/utils/commonUtils';
import { formatPercentNum } from '../../utils';
import ChartBubble, { TOP_CHARTS_TYPES } from '@/pages/intelligence/components/ChartBubble';

type TVisualisType = 'chart-treemap' | 'chart-line' | 'world-map' | 'discount-chart';

interface IVisualisProps {
  isLoading: boolean;
  visualisType?: TVisualisType;
  filter?: any;
  tableAction?: any;
  lineChartData?: any;
  entityType?: string;
  metric?: string;
  echartsMetricsCur?: string;
  pageId?: string;
  mapData?: any[];
  piecesMap?: any;
  nameMap?: any;
  headerLeftTitle?: string;
  leftContent?: string;
  dealMap?: any;
}

export default function Visualis(props: IVisualisProps) {
  const {
    isLoading,
    filter,
    tableAction,
    lineChartData,
    entityType = 'mobile',
    visualisType,
    metric,
    echartsMetricsCur,
    pageId = 'example',
    mapData = [],
    piecesMap,
    nameMap,
    headerLeftTitle,
    leftContent,
    dealMap,
  } = props;
  const getAddedParams = (chartItem: ITableData, locale: string) => {
    const { genre, sub_genre, metrics } = chartItem;
    const total = tableAction.selectedTotalValue[`${filter.sourceMetrics}_${metric}`];
    const isCn = locale === 'zh-CN';
    const value = metrics[`${filter.sourceMetrics}_${metric}`] || 0;
    const getName = (type: IGenreType) => {
      const genreObj = dealMap.fn(dealMap.data, dealMap.type, genre);
      const subObj = dealMap.fn(dealMap.data, dealMap.type, genre, sub_genre);
      switch (type) {
        case 'main':
          return isCn ? genreObj?.genre_cn || genre : genreObj?.genre_en || genre;
        case 'sub':
          return isCn ? subObj?.genre_cn || sub_genre : subObj?.genre_en || sub_genre;
        default:
          return `${isCn ? genreObj?.genre_cn || genre : genreObj?.genre_en || genre}-${
            isCn ? subObj?.genre_cn || sub_genre : subObj?.genre_en || sub_genre
          }`;
      }
    };
    return {
      name: getName(filter.genreType),
      value,
      formattedValue: valueFormat(value),
      percent: formatPercentNum(value / total),
      time: `${moment(filter.startDate).format('MMM, YYYY')} ~ ${moment(filter.endDate).format(
        'MMM, YYYY',
      )}`,
      isShow: true,
    };
  };

  const getRenderContent = (chartItem: any) => {
    const { name, formattedValue, percent } = chartItem;
    const html =
      '{name|' +
      `${name}` +
      '}\n' +
      `{num|${metric === 'downloads' ? '' : '$'}` +
      `${formattedValue}` +
      '}\n' +
      '{precent|' +
      `${percent}` +
      '}';
    return html;
  };

  const getTooltip = (chartItem: any) => {
    // console.log('chartItem', chartItem);
    const { name, formattedValue, percent, time } = chartItem;
    return ChartBubble({
      topChartsType: TOP_CHARTS_TYPES.GENRE_PROFILE_TOP_TREE,
      chartType: 'tree',
      secondTitleLeft: time,
      // data: [name + ' ' + formattedValue + ' ' + percent],
      otherTitleLeft: name,
      otherTitleRight: `${formattedValue}  ${percent}`,
      // secondTitleRight: echartsMetricsCur
    });
  };

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineChartData, // 折线 数据
    dateType: 'Month',
    imageType: entityType,
    metric,
    topChartsType: 'genreProfile-index',
    secondTitleRight: echartsMetricsCur,
  });

  /**
   * 根据类型返回不同的可视化图表组件
   * @param type - 可视化类型，支持 'chart-treemap' (矩形树图) 或 'chart-line' (折线图)
   * @returns 返回对应的图表组件，如果是树图且没有选中数据则返回 null
   * - chart-treemap: 返回矩形树组件，展示选中数据的层级结构
   * - chart-line: 返回折线图组件，展示数据趋势
   */
  const getVisualis = (type: TVisualisType) => {
    switch (type) {
      case 'chart-treemap':
        if (tableAction.selectedTotalValue) {
          return (
            <RectangleTrees
              uniqueKey={entityType}
              data={tableAction.selectedList}
              metric={metric}
              getAddedParams={getAddedParams}
              getRenderContent={getRenderContent}
              getTooltip={getTooltip}
              dealMap={dealMap}
            />
          );
        }
        return null;
      case 'discount-chart':
        return <LineChart height={300} lineOption={lineOption} notMerge />;
      case 'chart-line':
        return <LineChart height={300} lineOption={lineOption} notMerge />;
      case 'world-map':
        return (
          <WorldMap
            pageId={pageId}
            mapData={mapData}
            piecesMap={piecesMap}
            nameMap={nameMap}
            headerLeftTitle={headerLeftTitle}
            leftContent={leftContent}
          />
        );
      default:
        return null;
    }
  };

  /**
   * 判断图表是否无数据
   * @param type - 图表类型,'chart-treemap'为树图,'chart-line'为折线图
   * @returns 当不在加载状态且满足对应图表类型的条件时返回true,表示无数据
   */
  const getIsNoData = (type: TVisualisType) => {
    let anotherCond = true;
    if (type === 'chart-treemap') {
      anotherCond = !tableAction.selectedList?.length;
    } else if (type === 'chart-line') {
      anotherCond = !lineChartData?.[metric]?.length;
    } else if (type === 'discount-chart') {
      anotherCond = !lineChartData?.[metric]?.length;
    }
    return !isLoading && anotherCond;
  };

  return (
    <div className="chart-data">
      <Abnormal
        isLoading={isLoading}
        isNoData={getIsNoData(visualisType)}
        component={getVisualis(visualisType)}
      />
    </div>
  );
}
